/**
 * 侧栏权限快照 — 按 userId + workspaceId 读写，避免切换工作空间后沿用上一份权限。
 *
 * Callers: AccessSession、useStaleNavAccess
 */
import type {
  PlatformRole,
  WorkspacePermission,
  WorkspaceStatus,
} from "@metaadsmanager/tk-master-contracts";

const navPermissionsCacheKey = "tk-master.nav-permissions";

export type NavAccessSnapshot = {
  userId: string;
  workspaceId: string;
  permissions: WorkspacePermission[];
  platformRole: PlatformRole | null;
  workspaceStatus?: WorkspaceStatus;
  isWorkspaceOwner: boolean;
};

function parseSnapshot(raw: string | null): NavAccessSnapshot | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const record = parsed as Record<string, unknown>;
    if (typeof record.userId !== "string") return null;
    if (typeof record.workspaceId !== "string" || record.workspaceId.length === 0) {
      return null;
    }
    if (typeof record.isWorkspaceOwner !== "boolean") return null;
    if (!Array.isArray(record.permissions)) return null;
    const permissions = record.permissions.filter(
      (value): value is WorkspacePermission => typeof value === "string",
    );
    const platformRole =
      record.platformRole === "super_admin" || record.platformRole === null
        ? record.platformRole
        : null;
    const workspaceStatus =
      record.workspaceStatus === "active" ||
      record.workspaceStatus === "paused"
        ? record.workspaceStatus
        : undefined;
    return {
      userId: record.userId,
      workspaceId: record.workspaceId,
      permissions,
      platformRole,
      workspaceStatus,
      isWorkspaceOwner: record.isWorkspaceOwner,
    };
  } catch {
    return null;
  }
}

/**
 * 读取指定用户上次成功的侧栏权限快照。
 * 传入 workspaceId 时，只接受同一工作空间的快照。
 */
export function readCachedNavAccess(
  userId: string | undefined,
  workspaceId?: string | null,
): NavAccessSnapshot | null {
  if (typeof window === "undefined" || !userId) return null;
  try {
    const snapshot = parseSnapshot(
      window.localStorage.getItem(navPermissionsCacheKey),
    );
    if (!snapshot || snapshot.userId !== userId) return null;
    if (workspaceId && snapshot.workspaceId !== workspaceId) return null;
    return snapshot;
  } catch {
    return null;
  }
}

export function writeCachedNavAccess(snapshot: NavAccessSnapshot): boolean {
  if (typeof window === "undefined") return false;
  const next = JSON.stringify(snapshot);
  try {
    if (window.localStorage.getItem(navPermissionsCacheKey) === next) {
      return false;
    }
    window.localStorage.setItem(navPermissionsCacheKey, next);
    return true;
  } catch {
    return false;
  }
}

export function clearCachedNavAccess(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(navPermissionsCacheKey);
  } catch {
    // 清除失败不影响下一次 live 结算。
  }
}

/** 兼容旧测试与 AppShell 读取的 key。 */
export { navPermissionsCacheKey };
